import React from 'react';
import { NavLink, useRouteMatch } from '@react-router';
import { Box, Flex } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useTranslation } from '@shared/core';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { pxToRem } from '@withjoy/joykit/theme';
import { useStationeryEntitiesCount } from './useStationeryEntitiesCount';

const PrimaryNavCSS = {
  default: {
    ['&.active > span']: {
      borderBottomColor: 'mono12',
    },
  },
  disabled: {
    ['& > span']: {
      borderBottomColor: 'none',
    },
  },
  active: {
    ['& > span']: {
      borderBottomColor: 'mono12',
    },
    ['& > span:hover']: {
      borderBottomColor: 'mono12',
    },
  },
};

type PrimaryNavItemProps = {
  to: string;
  exact?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  activePaths?: string | string[];
  count?: number;
};

const PrimaryNavItem: React.FC<PrimaryNavItemProps> = ({ to, exact, children, onClick, disabled, activePaths, count }) => {
  // Underline if active and not disabled.
  // If 'activePaths' param passed, check if current route matches and force underline if it does.
  const customRouteMatching = useRouteMatch(activePaths ?? '');
  const override = activePaths && customRouteMatching;
  const underlineCSS = override ? PrimaryNavCSS.active : !disabled ? PrimaryNavCSS.default : PrimaryNavCSS.disabled;

  return (
    <Box
      __css={underlineCSS}
      paddingTop={1}
      paddingX={2}
      as={NavLink}
      onClick={onClick}
      exact={exact}
      to={to}
      typographyVariant={{ md: 'hed3' }}
      flex="none"
    >
      <Box
        _hover={{ borderBottomColor: 'mono5' }}
        transition={animationTransition('border-bottom-color')}
        borderBottom={'1px solid transparent'}
        borderBottomWidth={{ _: '2px', md: '1px' }}
        as="span"
        display="inline-flex"
        flexDirection="row"
        alignItems="center"
        height={{ _: pxToRem(76), md: 'auto' }}
        paddingY={{ _: '9px', md: 1 }}
      >
        {children}
        {!!count && (
          <Box
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            backgroundColor="#4951EF"
            verticalAlign={['top', null, 'middle']}
            borderRadius={pxToRem(12)}
            paddingX={pxToRem(6)}
            paddingY={pxToRem(2)}
            fontSize={pxToRem(15)}
            lineHeight={pxToRem(20)}
            minWidth={pxToRem(23)}
            fontWeight={600}
            color="white"
            marginLeft={{ _: 1, md: 3 }}
          >
            {count}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const PrimaryNav: React.FC = () => {
  const { getDraftsPath, getShopPath, getCardImaginePath, getOrdersPath, getFavoritesPath, routes } = useCardsRouterContext();
  const { primaryNavInteracted } = useCardTelemetry();
  const { t } = useTranslation('stationery');
  const { value: showImagineTab } = useFeatureValue('printImagineGalleryEnabled');
  const isFavoritesRouteEnabled = useFeatureValue('printFavoritesRouteEnabled').value === 'on';
  const isMobile = useIsMobileScreen();
  const { draftsCount, favoritesCount } = useStationeryEntitiesCount();

  const primaryNavTranslations = t('dashboard', 'primaryNav', isMobile ? 'mobile' : 'desktop');

  // Used to distinguish /cards/:occasion/:category/imagine from /cards/:occasion/:category
  const imagineRouteMatch = useRouteMatch(routes.imagine.paths);

  return (
    <Flex
      columnGap={{ _: '0.25rem', md: '1rem' }}
      marginLeft={{ _: '0.25rem', md: pxToRem(28) }}
      paddingRight={4}
      paddingLeft={{ _: 0, md: 4 }}
      alignItems="center"
      overflowX={{ _: 'scroll', sm2: 'unset' }}
      overflowY={{ _: 'hidden', sm2: 'unset' }}
      style={{
        scrollbarWidth: 'none',
      }}
    >
      <PrimaryNavItem
        to={getShopPath('wedding')}
        onClick={() => {
          primaryNavInteracted('home');
        }}
        disabled={imagineRouteMatch !== null}
      >
        {primaryNavTranslations.home()}
      </PrimaryNavItem>
      {isFavoritesRouteEnabled && (
        <PrimaryNavItem
          to={getFavoritesPath()}
          onClick={() => {
            primaryNavInteracted('favorites');
          }}
          count={favoritesCount}
        >
          {primaryNavTranslations.favorites()}
        </PrimaryNavItem>
      )}
      {showImagineTab && (
        <PrimaryNavItem
          to={getCardImaginePath('wedding')}
          onClick={() => {
            primaryNavInteracted('home');
          }}
          activePaths={routes.imagine.paths}
        >
          {primaryNavTranslations.imagine()}
        </PrimaryNavItem>
      )}

      <PrimaryNavItem
        to={getDraftsPath()}
        onClick={() => {
          primaryNavInteracted('drafts');
        }}
        count={draftsCount}
      >
        {primaryNavTranslations.drafts()}
      </PrimaryNavItem>
      <PrimaryNavItem
        to={getOrdersPath()}
        onClick={() => {
          primaryNavInteracted('orders');
        }}
      >
        {primaryNavTranslations.orders()}
      </PrimaryNavItem>
    </Flex>
  );
};
